import React, { useEffect, useState, useRef } from 'react';
import { ChatContextService } from '../services/chatContextService';

const ChatWidget = ({
  currentStep,
  orderData,
  displayRoutines,
  consultationData,
  isVisible = true,
  startOpen = false,
  initialMessage = '',
  onClose = () => { },
  enableAnimation = false
}) => {
  const [isOpen, setIsOpen] = useState(startOpen);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState(initialMessage);
  const [isTyping, setIsTyping] = useState(false);
  const [chatId, setChatId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Generate or retrieve chat ID
  useEffect(() => {
    let sessionChatId = sessionStorage.getItem('wonder-chat-id');
    if (!sessionChatId) {
      sessionChatId = `wonder_chat_${Math.random().toString(36).substr(2, 9)}_${Date.now()}`;
      sessionStorage.setItem('wonder-chat-id', sessionChatId);
    }
    setChatId(sessionChatId);
  }, []);

  // Initialize with welcome messages
  useEffect(() => {
    if (chatId && messages.length === 0) {
      const welcomeMessages = [
        {
          id: 'welcome-1',
          text: '✨ أهلاً وسهلاً في Wonder Beauties!',
          sender: 'bot',
          timestamp: new Date()
        },
        {
          id: 'welcome-2',
          text: '💄 أنا مساعدتك الشخصية للجمال والعناية بالبشرة',
          sender: 'bot',
          timestamp: new Date()
        },
        {
          id: 'welcome-3',
          text: '🌸 يمكنني مساعدتك في:\n• شرح نتائج تحليل بشرتك\n• توضيح المنتجات المقترحة\n• نصائح العناية اليومية\n• متابعة حالة طلبك\n\nما الذي تودين معرفته اليوم؟ 💅',
          sender: 'bot',
          timestamp: new Date()
        }
      ];
      setMessages(welcomeMessages);
    }
  }, [chatId, messages.length]);

  useEffect(() => {
    if (startOpen) {
      setIsOpen(true);
      setInputValue(initialMessage);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [startOpen, initialMessage]);

  // Auto scroll to bottom
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Get context for chat
  const getChatContext = async () => {
    try {
      const userContext = await ChatContextService.getUserChatContext();
      const stepContext = ChatContextService.getCurrentStepContext(currentStep, orderData, displayRoutines);

      return {
        ...userContext,
        ...stepContext,
        page_info: {
          page: "Wonder Beauties Consultation",
          url: window.location.href,
          timestamp: new Date().toISOString()
        }
      };
    } catch (error) {
      console.error('Error getting chat context:', error);
      return {};
    }
  };

  // Send message to n8n webhook
  const sendMessage = async () => {
    if (!inputValue.trim() || !chatId) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      text: inputValue,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    try {
      const webhookUrl = process.env.REACT_APP_N8N_CHAT_WEBHOOK_URL;
      if (!webhookUrl) {
        throw new Error('Webhook URL not configured');
      }

      const context = await getChatContext();

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chatId: chatId,
          message: inputValue,
          context: context,
          route: 'wonder-beauties-consultation',
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const botMessage = {
        id: `bot-${Date.now()}`,
        text: data.output || data.message || 'آسفة، لم أتمكن من فهم ذلك. يمكنك إعادة المحاولة؟',
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);

    } catch (error) {
      console.error('Error sending message:', error);

      const errorMessage = {
        id: `error-${Date.now()}`,
        text: 'آسفة، حدث خطأ في الاتصال. يمكنك المحاولة مرة أخرى؟ 🔄',
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  // Handle input key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Open chat widget
  const openChat = () => {
    setIsOpen(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  // Close chat widget
  const closeChat = () => {
    setIsOpen(false);
    onClose();
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Chat Launcher Button */}
      {!isOpen && (
        <div style={{ position: 'relative' }}>

          <button
            onClick={openChat}
            style={{
              position: 'fixed',
              bottom: '20px',
              right: '20px',
              background: '#A73A5D',
              color: 'white',
              border: 'none',
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '24px',
              zIndex: 1001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s ease',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.1)';
              e.target.style.background = '#8F2F4D';
              e.target.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.background = '#A73A5D';
              e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
            }}
          >
            💬
          </button>
        </div>
      )}

      {/* Chat Widget */}
      {isOpen && (
        <div
          translate="no"
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: '380px',
            height: '520px',
            background: 'white',
            borderRadius: '20px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1000,
            overflow: 'hidden',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            border: '1px solid rgba(167, 58, 93, 0.1)'
          }}
        >
          {/* Header */}
          <div
            style={{
              background: '#A73A5D',
              color: 'white',
              padding: '20px',
              fontWeight: 'bold',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '18px',
              borderRadius: '20px 20px 0 0'
            }}
          >
            <span>💄 مساعد Wonder Beauties</span>
            <button
              onClick={closeChat}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                border: 'none',
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              }}
            >
              ✕
            </button>
          </div>

          {/* Messages Body */}
          <div
            style={{
              flex: 1,
              padding: '20px',
              overflowY: 'auto',
              direction: 'rtl',
              textAlign: 'right'
            }}
          >
            {messages.map((message) => (
              <div
                key={message.id}
                style={{
                  marginBottom: '15px',
                  padding: '12px 16px',
                  borderRadius: message.sender === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  fontSize: '14px',
                  wordWrap: 'break-word',
                  maxWidth: '85%',
                  marginLeft: message.sender === 'user' ? 'auto' : '0',
                  marginRight: message.sender === 'bot' ? 'auto' : '0',
                  background: message.sender === 'user'
                    ? '#A73A5D'
                    : '#f8fafc',
                  color: message.sender === 'user' ? 'white' : '#1f2937',
                  border: message.sender === 'bot' ? '1px solid #e5e7eb' : 'none',
                  boxShadow: message.sender === 'user'
                    ? '0 2px 4px rgba(0, 0, 0, 0.1)'
                    : '0 2px 8px rgba(0, 0, 0, 0.05)',
                  whiteSpace: 'pre-line'
                }}
              >
                {message.text}
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div
                style={{
                  marginBottom: '15px',
                  padding: '12px 16px',
                  borderRadius: '16px 16px 16px 4px',
                  fontSize: '14px',
                  maxWidth: '85%',
                  background: '#f8fafc',
                  color: '#A73A5D',
                  border: '1px solid #e5e7eb',
                  fontStyle: 'italic'
                }}
              >
                جاري الكتابة... ✨
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Footer */}
          <div
            style={{
              padding: '16px 20px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              gap: '12px',
              background: 'white',
              borderRadius: '0 0 20px 20px'
            }}
          >
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="اكتبي سؤالك هنا... ✨"
              style={{
                flex: 1,
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                outline: 'none',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                direction: 'rtl',
                textAlign: 'right',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#A73A5D';
                e.target.style.boxShadow = '0 0 0 3px rgba(167, 58, 93, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
            <button
              onClick={sendMessage}
              disabled={!inputValue.trim() || isTyping}
              style={{
                background: inputValue.trim() && !isTyping
                  ? '#A73A5D'
                  : '#e5e7eb',
                color: inputValue.trim() && !isTyping ? 'white' : '#9ca3af',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '12px',
                cursor: inputValue.trim() && !isTyping ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                minWidth: '60px'
              }}
              onMouseEnter={(e) => {
                if (inputValue.trim() && !isTyping) {
                  e.target.style.transform = 'scale(1.05)';
                  e.target.style.background = '#8F2F4D';
                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (inputValue.trim() && !isTyping) {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.background = '#A73A5D';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              {isTyping ? '⏳' : '✨'}
            </button>
          </div>
        </div>
      )}

      {/* Mobile Responsive Styles & Animations */}
      <style jsx>{`
        @keyframes pulse-ring {
          0% {
            transform: scale(0.9);
            opacity: 1;
          }
          100% {
            transform: scale(1.3);
            opacity: 0;
          }
        }

        @keyframes gentle-bounce {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-3px);
          }
        }

        @media (max-width: 768px) {
          /* Chat widget responsive adjustments would go here */
        }
      `}</style>
    </>
  );
};

export default ChatWidget;